import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/nl';
import { GlobalStateService } from './core/services/global-state.service';

const LANG_KEY = 'vlugbezorgd_lang';
const SUPPORTED_LANGS = ['en', 'nl'];
const DEFAULT_TITLE = 'Vlugbezorgd Dashboard';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet],
    templateUrl: './app.component.html',
    styles: []
})
export class AppComponent {
    private translate = inject(TranslateService);
    private globalState = inject(GlobalStateService);
    private titleService = inject(Title);

    title = 'vlugbezorgd-admin';

    constructor() {
        this.translate.addLangs(SUPPORTED_LANGS);
        this.translate.setDefaultLang('nl');

        // Check saved language preference
        const savedLang = localStorage.getItem(LANG_KEY);
        if (savedLang && SUPPORTED_LANGS.includes(savedLang)) {
            this.translate.use(savedLang);
            dayjs.locale(savedLang);
        } else {
            // Auto-detect from browser
            const browserLang = this.translate.getBrowserLang() || 'nl';
            const langToUse = SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'nl';
            this.translate.use(langToUse);
            dayjs.locale(langToUse);
            localStorage.setItem(LANG_KEY, langToUse);
        }

        // Subscribe to language changes to persist + keep dayjs locale in sync.
        this.translate.onLangChange.subscribe(event => {
            localStorage.setItem(LANG_KEY, event.lang);
            dayjs.locale(event.lang);
        });

        // Browser tab title reflects the currently-selected shop so a shop
        // owner with multiple tabs open (one per site, common during cross-
        // shop comparison) can tell them apart at a glance. Falls back to
        // the platform default when no shop is picked yet (admin users,
        // first login, login screen).
        this.globalState.currentSite$.subscribe(site => {
            const name = site?.name?.trim();
            this.titleService.setTitle(name ? `${name} · Dashboard` : DEFAULT_TITLE);
        });

        // Global click listener to make all date inputs (including Material form fields)
        // fully clickable to open the native calendar picker anywhere in the application.
        document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            if (target) {
                // If the clicked element is a date input
                if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'date') {
                    try {
                        (target as any).showPicker();
                    } catch (e) {
                        console.warn('showPicker not supported', e);
                    }
                } else {
                    // If the click is inside a material form field wrapping a date input
                    const formField = target.closest('.mat-mdc-form-field');
                    if (formField) {
                        const dateInput = formField.querySelector('input[type="date"]') as any;
                        if (dateInput) {
                            try {
                                dateInput.showPicker();
                            } catch (e) {
                                // ignore
                            }
                        }
                    }
                }
            }
        });
    }
}
